// ============================================================================
// KLINISOL CRO - Reset Password Component
// Handles password reset via email link
// FDA 21 CFR Part 11 Compliant
// ============================================================================

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Loader2, CheckCircle, XCircle } from 'lucide-react';
import Navbar from './Navbar';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        // Validate password strength
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        const hasUppercase = /[A-Z]/.test(newPassword);
        const hasLowercase = /[a-z]/.test(newPassword);
        const hasNumber = /[0-9]/.test(newPassword);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

        if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
            setError('Password must contain uppercase, lowercase, number, and special character');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(true);
            } else {
                setError(data.message || 'Failed to reset password');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        }

        setLoading(false);
    };

    // No token provided
    if (!token) {
        return (
            <>
                <Navbar />
                <div className="min-h-screen bg-gradient-to-br from-[#475569] via-[#E91E63] to-[#475569] flex items-center justify-center px-6 py-12">
                    <div className="w-full max-w-md">
                        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
                            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
                            <p className="text-gray-600 mb-6">
                                This password reset link is invalid or has expired.
                            </p>
                            <button
                                onClick={() => navigate('/forgot-password')}
                                className="w-full px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-bold rounded-lg hover:from-[#334155] hover:to-[#1e293b] transition-all"
                            >
                                Request New Link
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-br from-[#475569] via-[#E91E63] to-[#475569] flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    {/* Logo Section */}
                    <div className="text-center mb-8">
                        <h1 className="text-5xl font-bold text-white mb-2">
                            KLIN<span className="text-[#E91E63]">i</span>SOL
                        </h1>
                        <p className="text-white text-xs opacity-75">Advancing Investigator-Initiated Research</p>
                    </div>

                    {/* Card */}
                    <div className="bg-white rounded-2xl shadow-2xl p-8">
                        {success ? (
                            // Success State
                            <div className="text-center">
                                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Reset</h2>
                                <p className="text-gray-600 mb-6">
                                    Your password has been successfully reset. You can now login with your new password.
                                </p>
                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-full px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-bold rounded-lg hover:from-[#334155] hover:to-[#1e293b] transition-all"
                                >
                                    Go to Login
                                </button>
                            </div>
                        ) : (
                            // Form State
                            <>
                                <div className="flex items-center justify-center gap-2 mb-6">
                                    <Lock className="w-6 h-6 text-[#475569]" />
                                    <h2 className="text-2xl font-bold text-gray-900">Reset Password</h2>
                                </div>

                                <p className="text-gray-600 text-sm mb-6 text-center">
                                    Enter your new password below.
                                </p>

                                {error && (
                                    <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                                        <p className="text-sm font-medium">{error}</p>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div>
                                        <label 
                                            htmlFor="newPassword" 
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                        >
                                            New Password
                                        </label>
                                        <input
                                            type="password"
                                            id="newPassword"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            placeholder="Enter new password"
                                            disabled={loading}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all disabled:bg-gray-100"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Min 8 chars: uppercase, lowercase, number, special char</p>
                                    </div>

                                    <div>
                                        <label 
                                            htmlFor="confirmPassword" 
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                        >
                                            Confirm Password
                                        </label>
                                        <input
                                            type="password"
                                            id="confirmPassword"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            placeholder="Confirm new password"
                                            disabled={loading}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all disabled:bg-gray-100"
                                        />
                                    </div>

                                    <button 
                                        type="submit" 
                                        disabled={loading}
                                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-bold rounded-lg hover:from-[#334155] hover:to-[#1e293b] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                <span>Resetting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Lock className="w-5 h-5" />
                                                <span>Reset Password</span>
                                            </>
                                        )}
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ResetPassword;
